import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import UserCardPayouts from 'components/UI/UserCard/UserCardPayouts'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { useDebounce } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { getPayoutsInvoices } from 'services/endpoints/earning'
import { type PayoutInvoice, usePayoutsCacheStore } from 'store/payoutsCacheStore'

import styles from './_Payouts.module.scss'

export const Payouts = () => {
  const [invoicesFeed, setInvoicesFeed] = useState<PayoutInvoice[]>([])
  const [dataReady, setDataReady] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string>('')

  const debouncedSearch = useDebounce(searchTerm, 1000)
  const { t } = useTranslation()

  const {
    makeKey,
    getInvoices,
    setInvoices,
    setLastFetched,
  } = usePayoutsCacheStore()

  const cacheKey = useMemo(() => makeKey(debouncedSearch), [makeKey, debouncedSearch])

  const hasCache = useMemo(() => {
    const cached = getInvoices(debouncedSearch)
    return cached.length > 0
  }, [cacheKey, getInvoices, debouncedSearch])

  const {
    data,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ['getPayoutsInvoices', debouncedSearch],
    async ({ pageParam = 1 }) =>
      await getPayoutsInvoices({ page: pageParam, search: debouncedSearch }),
    {
      getNextPageParam: (lastPage: any) => lastPage?.nextCursor,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )

  // 1) Hydrate immediately from cache for this search term
  useEffect(() => {
    const cached = getInvoices(debouncedSearch)
    if (cached.length) {
      setInvoicesFeed(cached)
      setDataReady(true)
    } else {
      // no cache -> not ready until first fetch lands
      setDataReady(false)
      setInvoicesFeed([])
    }
  }, [cacheKey])

  // 2) Update with fresh data when it arrives
useEffect(() => {
  if (!data) return

  const fresh: PayoutInvoice[] = data.pages
    .flatMap(
      (page: any) =>
        page?.page?.data?.data?.results ??
        page?.data?.results ??
        []
    )
    .filter(Boolean)

  setInvoicesFeed(fresh)
  setInvoices(debouncedSearch, fresh)
  setLastFetched(debouncedSearch, Date.now())
  setDataReady(true)
}, [data, debouncedSearch, setInvoices, setLastFetched])

  const showLoader = !dataReady && !hasCache && isFetching && invoicesFeed.length === 0

return (
  <div className="payouts-container">
    <WithHeaderSection
      headerSection={
        <LayoutHeader
          type="search-with-buttons"
          searchValue={searchTerm}
          searchFn={(term: string) => setSearchTerm(term)}
          clearFn={() => setSearchTerm('')}
          additionalProps={{ placeholder: t('searchInvoice') }}
          buttons={[]}
        />
      }
    >
      <div className={styles.payouts}>
        {!dataReady && invoicesFeed.length === 0 && (
          <div className={styles.payouts_loading}>
            <BeatLoader size={8} />
          </div>
        )}

        {dataReady && invoicesFeed.length === 0 && (
          <div className={styles.payouts_empty}>
            <p>{t('No Payouts')}</p>
          </div>
        )}

        {invoicesFeed.map((invoice, index) => {
          const isLast = invoicesFeed.length === index + 1

          const card = (
            <UserCardPayouts
              avatarUrl={invoice.avatar ?? undefined}
              earningsCardType={invoice.status}
              date={invoice.date}
              invoiceNmbr={invoice.invoiceNmbr}
              amount={invoice.amount}
            />
          )

          return isLast ? (
            <InView
              key={invoice.invoiceId}
              as="div"
              threshold={0.1}
              triggerOnce
              onChange={(inView) => {
                if (inView && !isFetchingNextPage) {
                  fetchNextPage()
                }
              }}
            >
              {card}
            </InView>
          ) : (
            <div key={invoice.invoiceId}>{card}</div>
          )
        })}
      </div>
    </WithHeaderSection>
  </div>
)
}
